import { useState } from 'react';
import { X, Shield, QrCode } from 'lucide-react';
import { generateSecret, generateTOTPUri, generateQRCode, verifyTOTP } from '../lib/totp';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface TwoFactorSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function TwoFactorSetupModal({ isOpen, onClose, onSuccess }: TwoFactorSetupModalProps) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [secret, setSecret] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleStart = async () => {
    try {
      setLoading(true);
      const newSecret = generateSecret();
      setSecret(newSecret);

      if (!user?.email) throw new Error('User email not found');
      const uri = generateTOTPUri(user.email, newSecret);
      const qrCodeUrl = await generateQRCode(uri);
      setQrCode(qrCodeUrl);
      setStep(2);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    try {
      setLoading(true);
      setError('');

      if (!token) {
        setError('Veuillez entrer le code');
        return;
      }

      const isValid = verifyTOTP(token, secret);
      if (!isValid) {
        setError('Code invalide');
        return;
      }

      // Save the secret and enable 2FA
      const { error: updateError } = await supabase
        .from('users')
        .update({
          totp_secret: secret,
          totp_enabled: true,
          totp_verified: true
        })
        .eq('id', user?.id);

      if (updateError) throw updateError;

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200]">
      <div className="bg-white rounded-lg p-6 w-full max-w-md relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X size={20} />
        </button>

        <div className="space-y-6">
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">
              Configuration de la double authentification
            </h2>
          </div>

          {step === 1 ? (
            <div className="space-y-4">
              <p className="text-gray-600">
                La double authentification ajoute une couche de sécurité supplémentaire à votre compte.
                Vous aurez besoin d'une application d'authentification comme Google Authenticator.
              </p>

              {error && (
                <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <button
                onClick={handleStart}
                disabled={loading}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Chargement...' : 'Commencer la configuration'}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="flex justify-center">
                  {qrCode && (
                    <div className="p-4 bg-white rounded-lg shadow-sm">
                      <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                    </div>
                  )}
                </div>

                <div className="text-sm text-gray-600">
                  <p className="font-medium mb-2">Instructions :</p>
                  <ol className="list-decimal list-inside space-y-2">
                    <li>Ouvrez votre application d'authentification</li>
                    <li>Scannez le QR code ci-dessus</li>
                    <li>Entrez le code à 6 chiffres généré</li>
                  </ol>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code de vérification
                  </label>
                  <input
                    type="text"
                    value={token}
                    onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                </div>

                {error && (
                  <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                    {error}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Annuler
                </button>
                <button
                  onClick={handleVerify}
                  disabled={loading || token.length !== 6}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Vérification...' : 'Activer'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}